import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { api } from "@/lib/api";
import { EntityManager } from "@/components/admin/EntityManager";
import { FileUpload } from "@/components/admin/FileUpload";
import { DepotBookings } from "@/components/admin/DepotBookings";
import { NewsletterSubscribers } from "@/components/admin/NewsletterSubscribers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, Radio, Home, Loader2, Trash2, Plus, Mail } from "lucide-react";

const STATUS_OPTS = ["NYTT", "UPPDATERAT", "PRELIMINÄRT", "OFFICIELLT"];
const TIER_OPTS = ["Huvudpartner", "Officiell Partner", "Partner"];

const TABS = [
  { id: "settings", label: "Hero & Live" },
  { id: "news", label: "Nyheter" },
  { id: "documents", label: "Dokument" },
  { id: "infosections", label: "Tävlingsinfo" },
  { id: "schedule", label: "Program" },
  { id: "sponsors", label: "Partners" },
  { id: "spectators", label: "Publikplatser" },
  { id: "faq", label: "FAQ" },
  { id: "contacts", label: "Kontaktpersoner" },
  { id: "history", label: "Historia" },
  { id: "videos", label: "Videor" },
  { id: "depot", label: "Depåbokningar" },
  { id: "newsletter", label: "Nyhetsbrev" },
  { id: "messages", label: "Meddelanden" },
];

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#0B0C0E] px-5">
      <form onSubmit={submit} data-testid="admin-login" className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#14161A] p-8">
        <div className="font-display text-3xl font-black uppercase text-white text-center">NOVEMBER<span className="text-amber-500">KÅSAN</span></div>
        <div className="mt-1 text-center font-mono text-xs uppercase tracking-widest text-white/40">Adminpanel</div>
        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-white/70 text-xs uppercase">E-post</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-black/40 border-white/15" data-testid="login-email" />
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase">Lösenord</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-black/40 border-white/15" data-testid="login-password" />
          </div>
          {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-amber-500 text-black hover:bg-amber-400">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Logga in"}
          </Button>
        </div>
        <Link to="/" className="mt-6 block text-center text-xs text-white/40 hover:text-amber-500">← Tillbaka till sidan</Link>
      </form>
    </div>
  );
}

function SettingsEditor() {
  const { settings, refresh } = useData();
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const stats = form.stats || [];
  const liveLinks = form.live_links || [];
  const social = form.social || {};

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings(form);
      await refresh();
      toast.success("Inställningar sparade!");
    } catch { toast.error("Kunde inte spara"); }
    finally { setSaving(false); }
  };

  const inp = "mt-1.5 bg-black/40 border-white/15";
  const card = "rounded-xl border border-white/8 bg-[#14161A] p-6 space-y-4";

  return (
    <div className="space-y-6">
      {/* Live mode */}
      <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 font-display text-2xl font-black uppercase text-white"><Radio className="w-5 h-5 text-red-500" /> Live Mode</div>
            <p className="text-sm text-white/50 mt-1">Aktivera under tävlingen för att visa live-banner och snabblänkar.</p>
          </div>
          <Switch checked={!!form.live_mode} onCheckedChange={(v) => set("live_mode", v)} data-testid="live-mode-toggle" />
        </div>
        {form.live_mode && (
          <div className="mt-5 space-y-4">
            <div><Label className="text-white/70 text-xs uppercase">Live-titel</Label><Input value={form.live_title || ""} onChange={(e) => set("live_title", e.target.value)} className={inp} /></div>
            <div>
              <Label className="text-white/70 text-xs uppercase">Snabblänkar</Label>
              {liveLinks.map((l, i) => (
                <div key={i} className="flex gap-2 mt-2">
                  <Input placeholder="Etikett" value={l.label} onChange={(e) => { const n = [...liveLinks]; n[i] = { ...n[i], label: e.target.value }; set("live_links", n); }} className="bg-black/40 border-white/15" />
                  <Input placeholder="URL" value={l.url} onChange={(e) => { const n = [...liveLinks]; n[i] = { ...n[i], url: e.target.value }; set("live_links", n); }} className="bg-black/40 border-white/15" />
                  <Button variant="ghost" onClick={() => set("live_links", liveLinks.filter((_, x) => x !== i))} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              <Button variant="ghost" onClick={() => set("live_links", [...liveLinks, { label: "", url: "" }])} className="mt-2 text-amber-500"><Plus className="w-4 h-4 mr-1" /> Lägg till länk</Button>
            </div>
          </div>
        )}
      </div>

      {/* Hero */}
      <div className={card}>
        <h3 className="font-display text-xl font-bold uppercase text-white">Hero</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-white/70 text-xs uppercase">Titel</Label><Input value={form.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className={inp} data-testid="hero-title-input" /></div>
          <div><Label className="text-white/70 text-xs uppercase">Plats</Label><Input value={form.hero_location || ""} onChange={(e) => set("hero_location", e.target.value)} className={inp} /></div>
          <div><Label className="text-white/70 text-xs uppercase">Datum</Label><Input value={form.hero_date || ""} onChange={(e) => set("hero_date", e.target.value)} className={inp} /></div>
          <div><Label className="text-white/70 text-xs uppercase">Anmälan URL</Label><Input value={form.anmalan_url || ""} onChange={(e) => set("anmalan_url", e.target.value)} className={inp} placeholder="https://..." /></div>
        </div>
        <div><Label className="text-white/70 text-xs uppercase">Tagline</Label><Input value={form.hero_tagline || ""} onChange={(e) => set("hero_tagline", e.target.value)} className={inp} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-white/70 text-xs uppercase">Hero-bild</Label><div className="mt-1.5"><FileUpload value={form.hero_image} onChange={(u) => set("hero_image", u)} kind="image" /></div></div>
          <div><Label className="text-white/70 text-xs uppercase">Hero-video (valfritt)</Label><div className="mt-1.5"><FileUpload value={form.hero_video_url} onChange={(u) => set("hero_video_url", u)} kind="file" accept="video/*" /></div></div>
        </div>
        <div><Label className="text-white/70 text-xs uppercase">Countdown-mål (ISO, t.ex. 2026-11-07T09:00:00)</Label><Input value={form.countdown_target || ""} onChange={(e) => set("countdown_target", e.target.value)} className={inp} /></div>
      </div>

      {/* Intro */}
      <div className={card}>
        <h3 className="font-display text-xl font-bold uppercase text-white">Introduktion</h3>
        <div><Label className="text-white/70 text-xs uppercase">Rubrik</Label><Input value={form.intro_heading || ""} onChange={(e) => set("intro_heading", e.target.value)} className={inp} /></div>
        <div><Label className="text-white/70 text-xs uppercase">Brödtext</Label><Textarea value={form.intro_body || ""} onChange={(e) => set("intro_body", e.target.value)} className={inp} rows={4} /></div>
        <div><Label className="text-white/70 text-xs uppercase">Introbild</Label><div className="mt-1.5"><FileUpload value={form.intro_image} onChange={(u) => set("intro_image", u)} kind="image" /></div></div>
        <div>
          <Label className="text-white/70 text-xs uppercase">Statistik</Label>
          {stats.map((s, i) => (
            <div key={i} className="flex gap-2 mt-2">
              <Input placeholder="Värde" value={s.value} onChange={(e) => { const n = [...stats]; n[i] = { ...n[i], value: e.target.value }; set("stats", n); }} className="bg-black/40 border-white/15" />
              <Input placeholder="Etikett" value={s.label} onChange={(e) => { const n = [...stats]; n[i] = { ...n[i], label: e.target.value }; set("stats", n); }} className="bg-black/40 border-white/15" />
              <Button variant="ghost" onClick={() => set("stats", stats.filter((_, x) => x !== i))} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button variant="ghost" onClick={() => set("stats", [...stats, { value: "", label: "" }])} className="mt-2 text-amber-500"><Plus className="w-4 h-4 mr-1" /> Lägg till statistik</Button>
        </div>
      </div>

      {/* Partner CTA + Social */}
      <div className={card}>
        <h3 className="font-display text-xl font-bold uppercase text-white">Bli partner & Sociala medier</h3>
        <div><Label className="text-white/70 text-xs uppercase">CTA-rubrik</Label><Input value={form.partner_cta_heading || ""} onChange={(e) => set("partner_cta_heading", e.target.value)} className={inp} /></div>
        <div><Label className="text-white/70 text-xs uppercase">CTA-text</Label><Textarea value={form.partner_cta_body || ""} onChange={(e) => set("partner_cta_body", e.target.value)} className={inp} rows={2} /></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label className="text-white/70 text-xs uppercase">Instagram</Label><Input value={social.instagram || ""} onChange={(e) => set("social", { ...social, instagram: e.target.value })} className={inp} /></div>
          <div><Label className="text-white/70 text-xs uppercase">Facebook</Label><Input value={social.facebook || ""} onChange={(e) => set("social", { ...social, facebook: e.target.value })} className={inp} /></div>
          <div><Label className="text-white/70 text-xs uppercase">YouTube</Label><Input value={social.youtube || ""} onChange={(e) => set("social", { ...social, youtube: e.target.value })} className={inp} /></div>
        </div>
      </div>

      <div className="sticky bottom-4">
        <Button onClick={save} disabled={saving} data-testid="settings-save" className="w-full bg-amber-500 text-black hover:bg-amber-400 h-12 text-base font-bold uppercase tracking-widest">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Spara inställningar"}
        </Button>
      </div>
    </div>
  );
}

function Messages() {
  const [msgs, setMsgs] = useState(null);
  useEffect(() => { api.listMessages().then(setMsgs).catch(() => setMsgs([])); }, []);
  if (msgs === null) return <p className="text-white/40 font-mono text-sm">Laddar...</p>;
  const del = async (id) => { await api.removeMessage(id); setMsgs(msgs.filter((m) => m.id !== id)); };
  return (
    <div>
      <h2 className="font-display text-3xl font-black uppercase text-white mb-6">Meddelanden</h2>
      {msgs.length === 0 && <p className="text-white/40 font-mono text-sm">Inga meddelanden.</p>}
      <div className="space-y-3">
        {msgs.map((m) => (
          <div key={m.id} className="rounded-lg border border-white/8 bg-[#14161A] p-4" data-testid={`message-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-white">{m.name} <span className="text-white/40 text-sm font-normal">· {m.email}</span></div>
                {m.subject && <div className="text-amber-500 text-sm">{m.subject}</div>}
              </div>
              <button onClick={() => del(m.id)} className="p-2 text-white/40 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="mt-2 text-white/70 text-sm whitespace-pre-line">{m.message}</p>
            <div className="mt-2 font-mono text-[10px] text-white/30">{new Date(m.created_at).toLocaleString("sv-SE")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("settings");

  if (user === null) return <div className="min-h-screen grid place-items-center bg-[#0B0C0E]"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  if (!user) return <Login />;
  if (user.role !== "admin") return (
    <div className="min-h-screen grid place-items-center bg-[#0B0C0E] px-5" data-testid="admin-no-access">
      <div className="text-center max-w-sm">
        <div className="font-display text-3xl font-black uppercase text-white">Åtkomst nekad</div>
        <p className="mt-3 text-white/60 text-sm">Ditt konto har inte behörighet till CMS:et. Tidtagningskontot postar på anslagstavlan.</p>
        <div className="mt-6 flex flex-col gap-2">
          <Link to="/tavla-admin" className="rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-bold uppercase tracking-widest text-black hover:bg-amber-400">Till anslagstavlan</Link>
          <button onClick={logout} data-testid="no-access-logout" className="text-xs text-white/40 hover:text-red-400">Logga ut</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0C0E] flex flex-col lg:flex-row">
      <aside className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0A0B0D] lg:min-h-screen">
        <div className="p-5 border-b border-white/10">
          <div className="font-display text-xl font-black uppercase text-white">NOVEMBER<span className="text-amber-500">KÅSAN</span></div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">CMS · {user.name}</div>
        </div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`shrink-0 text-left rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === t.id ? "bg-amber-500 text-black" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 lg:mt-auto space-y-1 border-t border-white/10">
          <Link to="/" className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-white"><Home className="w-4 h-4" /> Visa sidan</Link>
          <button onClick={logout} data-testid="admin-logout" className="w-full flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm text-white/60 hover:bg-white/5 hover:text-red-400"><LogOut className="w-4 h-4" /> Logga ut</button>
        </div>
      </aside>

      <main className="flex-1 p-6 sm:p-10 max-w-5xl">
        {tab === "settings" && <SettingsEditor />}
        {tab === "depot" && <DepotBookings />}
        {tab === "newsletter" && <NewsletterSubscribers />}
        {tab === "messages" && <Messages />}
        {tab === "news" && <EntityManager type="news" title="Nyheter" labelField="title" subLabelField="date" fields={[
          { name: "title", label: "Rubrik", type: "text" }, { name: "date", label: "Datum", type: "text" },
          { name: "status", label: "Status", type: "select", options: STATUS_OPTS, optional: true }, { name: "tag", label: "Tagg", type: "text" },
          { name: "excerpt", label: "Ingress", type: "textarea" }, { name: "body", label: "Full artikel", type: "textarea" },
          { name: "image", label: "Bild", type: "image" }, { name: "link", label: "Extern länk", type: "text" }, { name: "file_url", label: "Dokument/PDF", type: "file" },
        ]} />}
        {tab === "documents" && <EntityManager type="documents" title="Dokument" labelField="title" subLabelField="category" fields={[
          { name: "title", label: "Rubrik", type: "text" }, { name: "category", label: "Kategori", type: "select", options: ["PM & Tilläggsregler", "Startlistor", "Kartor", "Resultat"] },
          { name: "status", label: "Status", type: "select", options: STATUS_OPTS, optional: true }, { name: "date", label: "Datum", type: "text" },
          { name: "description", label: "Beskrivning", type: "textarea" }, { name: "file_url", label: "PDF-fil", type: "file" }, { name: "external_url", label: "Extern URL", type: "text" },
        ]} />}
        {tab === "infosections" && <EntityManager type="infosections" title="Tävlingsinfo" labelField="heading" fields={[
          { name: "heading", label: "Rubrik", type: "text" }, { name: "body", label: "Text", type: "textarea" },
          { name: "icon", label: "Ikon (calendar, map-pin, users, wrench, clipboard-list, id-card, info)", type: "text" },
        ]} />}
        {tab === "schedule" && <EntityManager type="schedule" title="Program" labelField="label" subLabelField="day" fields={[
          { name: "day", label: "Dag", type: "text" }, { name: "time", label: "Tid", type: "text" },
          { name: "label", label: "Moment", type: "text" }, { name: "note", label: "Notering", type: "text" },
        ]} />}
        {tab === "sponsors" && <EntityManager type="sponsors" title="Partners" labelField="name" subLabelField="tier" fields={[
          { name: "name", label: "Företagsnamn", type: "text" }, { name: "tier", label: "Nivå", type: "select", options: TIER_OPTS },
          { name: "logo_url", label: "Logo", type: "image" }, { name: "url", label: "URL", type: "text" }, { name: "description", label: "Beskrivning", type: "textarea" },
        ]} />}
        {tab === "gallery" && <EntityManager type="gallery" title="Galleri" labelField="title" subLabelField="category" fields={[
          { name: "title", label: "Titel", type: "text" }, { name: "category", label: "Kategori", type: "text" }, { name: "image", label: "Bild", type: "image" },
        ]} />}
        {tab === "spectators" && <EntityManager type="spectators" title="Publikplatser" labelField="name" fields={[
          { name: "name", label: "Namn", type: "text" }, { name: "image", label: "Bild", type: "image" }, { name: "description", label: "Beskrivning", type: "textarea" },
          { name: "recommended_time", label: "Rekommenderad tid", type: "text" }, { name: "parking", label: "Parkering", type: "text" },
          { name: "gps", label: "GPS", type: "text" }, { name: "maps_url", label: "Google Maps-länk", type: "text" },
        ]} />}
        {tab === "faq" && <EntityManager type="faq" title="FAQ" labelField="question" subLabelField="category" fields={[
          { name: "category", label: "Kategori", type: "select", options: ["Förare", "Publik", "Parkering", "Tävlingsinformation", "Anmälan"] },
          { name: "question", label: "Fråga", type: "text" }, { name: "answer", label: "Svar", type: "textarea" },
        ]} />}
        {tab === "contacts" && <EntityManager type="contacts" title="Kontaktpersoner" labelField="name" subLabelField="area" fields={[
          { name: "name", label: "Namn", type: "text" }, { name: "role", label: "Roll", type: "text" }, { name: "area", label: "Område", type: "text" },
          { name: "email", label: "E-post", type: "text" }, { name: "phone", label: "Telefon", type: "text" },
        ]} />}
        {tab === "history" && <EntityManager type="history" title="Historia" labelField="title" subLabelField="year" fields={[
          { name: "year", label: "År", type: "text" }, { name: "title", label: "Rubrik", type: "text" }, { name: "description", label: "Beskrivning", type: "textarea" },
        ]} />}
        {tab === "videos" && <EntityManager type="videos" title="Videor" labelField="title" subLabelField="year" fields={[
          { name: "title", label: "Titel", type: "text" }, { name: "youtube_url", label: "YouTube-länk", type: "text" },
          { name: "year", label: "År (valfritt)", type: "text" }, { name: "description", label: "Beskrivning", type: "textarea" },
        ]} />}
      </main>
    </div>
  );
}
